import { partition } from "lodash";
import * as React from "react";

import { useUserConfig } from "@/shared/domain/config/hooks/useUserConfig";
import { useLayouts } from "@/shared/domain/layouts/hooks";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";

import {
  groupLayoutsIntoFolders,
  toLayoutOption,
  type LayoutFolderGroup,
  type LayoutOption,
} from "./layoutFolders";

/** How many layouts the "Recently modified" section shows. */
const RECENT_LIMIT = 10;

interface LayoutOptionsResult {
  recentOptions: LayoutOption[];
  personalGroups: LayoutFolderGroup[];
  teamGroups: LayoutFolderGroup[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Groups the org's layouts into folders, split by Personal/Team accessibility and filtered by
 * `searchQuery` (matched against layout and folder names). Also returns the most recently modified
 * layouts (unfiltered) for the "Recently modified" shortcut.
 */
export function useLayoutOptions(searchQuery = ""): LayoutOptionsResult {
  const layoutsQuery = useLayouts();

  const usersDefaultLayoutQuery = useUserConfig<string>(
    "user_default_layout_id",
  );
  const defaultLayoutId = usersDefaultLayoutQuery.data ?? null;

  // `modified` is an ISO 8601 string, so a lexicographic sort is chronological.
  const recentOptions = React.useMemo(() => {
    return [...(layoutsQuery.data ?? [])]
      .sort((a, b) => b.modified.localeCompare(a.modified))
      .slice(0, RECENT_LIMIT)
      .map((layout) => toLayoutOption(layout, defaultLayoutId));
  }, [layoutsQuery.data, defaultLayoutId]);

  const { personalGroups, teamGroups } = React.useMemo(() => {
    const [personalLayouts, teamLayouts] = partition(
      layoutsQuery.data ?? [],
      (layout) => layout.accessibility === LayoutAccessibility.User,
    );

    return {
      personalGroups: groupLayoutsIntoFolders(
        personalLayouts,
        defaultLayoutId,
        searchQuery,
      ),
      teamGroups: groupLayoutsIntoFolders(
        teamLayouts,
        defaultLayoutId,
        searchQuery,
      ),
    };
  }, [layoutsQuery.data, defaultLayoutId, searchQuery]);

  return {
    recentOptions,
    personalGroups,
    teamGroups,
    isPending: layoutsQuery.isPending,
    isError: layoutsQuery.isError,
    refetch: () => {
      void layoutsQuery.refetch();
    },
  };
}
